import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isEmpty} from "lodash";
import {clsx} from "clsx";
import popupStore from "../../store/popupStore";
import addressStore from "../../store/addressStore";
import ImageBasic from "../Common/Image/ImageBasic";
import IMG from "../../constants/image";
import FutaOrderConfirmationModular from "../shipping/FutaOrderConfirmationModular";
import appStore from "../../store/appStore";
import {isMobile} from "react-device-detect";
import {StoreFormAddress} from "./StoreFormAddress";
import {text} from "../../constants/text";
import {fetchAddresses} from "../../services/addressServices";

export function ListAddressSeller({addressPickup}) {
    // Create refs for the address list container and selected item
    const lastItemRef = useRef(null);
    const addressListRef = useRef(null);
    const selectedItemRef = useRef(null);
    // Observer for detecting when the last item is visible
    const observer = useRef(null);

    const {setPopup, setPopupMobile} = popupStore() || {};
    const {setAddressChoice, setAnimate, setActiveTabConfirm, animate} = appStore() || {};
    const {addressAll, detailAddress, setDetailAddress} = addressStore();
    const [selectedOption, setSelectedOption] = useState('');

    const [hasInitialized, setHasInitialized] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalPages, setTotalPages] = useState(2);
    // State for infinite scrolling
    const [itemsPerPage] = useState(20); // Number of addresses displayed on each page
    const [currentPage, setCurrentPage] = useState(1);


    const [mergedAddresses, setMergedAddresses] = useState([]);

    const handleOptionChange = (item) => {
        setSelectedOption(item?.id);
        setAddressChoice(item)
        if (!isEmpty(detailAddress)) setDetailAddress({})
        setAnimate({direction: "right"})
        if (isMobile) {
            setActiveTabConfirm('pickup')
            return setPopupMobile({
                isOpenMobile: true,
                data: {
                    height: 'h-[90%]',
                    top: 'top-[12%]',
                    children: <FutaOrderConfirmationModular
                        onClose={() => setPopupMobile({})}/>
                }
            })
        }
        return setPopup({
            isOpen: true,
            data: {
                bgClass: 'bg-[#FAFAFC]',
                buttonCloseClass: 'top-5 right-6',
                customContent: () => {
                    return <FutaOrderConfirmationModular
                        onClose={() => setPopup({})}/>
                }
            }
        })
    };
    const addressDefault = useMemo(() => {
        if (isEmpty(mergedAddresses?.items)) return null
        return mergedAddresses.find((item) => item.isPrimary)
    }, [mergedAddresses])

    const handleAddAddress = () => {
        if (isMobile) {
            setAnimate({direction: "left"})
            setPopupMobile({
                isOpenMobile: true,
                data: {
                    buttonCloseClass: 'top-6 right-6',
                    children: <StoreFormAddress
                        isOrderConfirm
                        addressPickup={addressPickup}
                        title={text.addAddressNew}
                        isNewAddress/>,
                }
            })
        } else {
            setAnimate({direction: "left"})
            setPopup({
                isOpen: true,
                data: {
                    buttonCloseClass: 'top-6 right-6',
                    customContent: () => <StoreFormAddress
                        isOrderConfirm
                        addressPickup={addressPickup}
                        title={text.addAddress}
                        isNewAddress/>,
                }
            })
        }
    };
    const handleBack = () => {
        setAnimate({direction: "right"})
        if (isMobile) {
            setActiveTabConfirm('pickup')
            return setPopupMobile({
                isOpenMobile: true,
                data: {
                    height: 'h-[90%]',
                    top: 'top-[12%]',
                    children: <FutaOrderConfirmationModular
                        onClose={() => setPopupMobile({})}/>
                }
            })
        }
        return setPopup({
            isOpen: true,
            data: {
                bgClass: 'bg-[#FAFAFC]',
                buttonCloseClass: 'top-5 right-6',
                customContent: () => {
                    return <FutaOrderConfirmationModular
                        onClose={() => setPopup({})}/>
                }
            }
        })
    }

    // Function to merge addresses and remove duplicates
    const mergeAddresses = (existingAddresses, newAddresses) => {
        if (!newAddresses || !Array.isArray(newAddresses)) return existingAddresses;

        // Create a Map using address ID as key for fast lookup
        const addressMap = new Map();

        // Add existing addresses to the map
        existingAddresses.forEach(address => {
            addressMap.set(address.id, address);
        });

        // Add new addresses to the map (overwrites duplicates)
        newAddresses.forEach(address => {
            // Only add if not already in the map
            if (!addressMap.has(address.id)) {
                addressMap.set(address.id, address);
            }
        });

        // Convert map values back to array
        return Array.from(addressMap.values());
    };
    // Function to load more addresses
    const loadMoreAddresses = useCallback(() => {
        if (isLoadingMore || currentPage >= totalPages) return;
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        fetchAddresses({page: nextPage, size: itemsPerPage})
            .then((res) => {
                if (res?.count) {
                    // Calculate total pages if only totalItems is provided
                    setTotalPages(Math.ceil(res.total / itemsPerPage));
                }

                // Merge new addresses with existing ones
                if (res?.items && Array.isArray(res.items)) {
                    setMergedAddresses(prevAddresses => mergeAddresses(prevAddresses, res.items));
                }

                setCurrentPage(nextPage);
            })
            .finally(() => {
                setIsLoadingMore(false);
            });
    }, [currentPage, totalPages, isLoadingMore, fetchAddresses]);

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        // If there's no last item or we're already loading, do nothing
        if (!lastItemRef.current || isLoadingMore) return;

        // Disconnect any existing observer
        if (observer.current) observer.current.disconnect();

        // Create a new observer
        observer.current = new IntersectionObserver(entries => {
            // If the last item is visible and we're not at the end
            if (entries[0]?.isIntersecting && currentPage < totalPages) {
                loadMoreAddresses();
            }
        }, {
            root: addressListRef.current,
            threshold: 0.1 // 10% visibility
        });

        // Start observing the last item
        observer.current.observe(lastItemRef.current);

        // Cleanup
        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [mergedAddresses, currentPage, totalPages, isLoadingMore, loadMoreAddresses]);

    useEffect(() => {
        if (!isEmpty(addressPickup)) setSelectedOption(addressPickup?.id);
        else if (!isEmpty(addressDefault)) {
            setSelectedOption(addressDefault?.id);
        }
    }, [addressDefault]);
    // Effect to scroll to the selected option when it changes
    useEffect(() => {
        if (selectedItemRef.current && addressListRef.current) {
            // Get the position of the selected item relative to the container
            const container = addressListRef.current;
            const selectedItem = selectedItemRef.current;

            // Check if the selected item is not fully visible in the container
            const containerRect = container.getBoundingClientRect();
            const selectedRect = selectedItem.getBoundingClientRect();

            // If the selected item is below the visible area
            if (selectedRect.bottom > containerRect.bottom) {
                // Scroll the item into view
                selectedItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
            // If the selected item is above the visible area
            else if (selectedRect.top < containerRect.top) {
                selectedItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [selectedOption]);

    // Initial data loading
    useEffect(() => {
        if (!hasInitialized) {
            fetchAddresses({page: currentPage, size: itemsPerPage}).then((res) => {
                // Update total pages based on API response
                if (res?.count) {
                    // Calculate total pages if only totalItems is provided
                    setTotalPages(Math.ceil(res?.count / itemsPerPage));
                }

                // Initialize merged addresses with first page
                if (res?.items && Array.isArray(res.items)) {
                    setMergedAddresses(res.items);
                }
                setHasInitialized(true);
            });
        }
    }, [hasInitialized]);

    // Update merged addresses when addressAll changes (e.g., after add/edit address)
    useEffect(() => {
        if (addressAll?.items && Array.isArray(addressAll.items) && addressAll.items.length > 0) {
            // Only update on first load or after address operations (not on pagination)
            if (currentPage === 1 || addressAll.items.length !== mergedAddresses.length) {
                setMergedAddresses(prevAddresses => mergeAddresses(prevAddresses, addressAll.items));
            }
        }
    }, [addressAll?.items]);
    if (isEmpty(mergedAddresses)) return null;

    return (
        <div
            className={clsx("bg-white md:min-w-[523px] w-full text-sm px-4 md:px-0 md:space-y-4",
                isMobile && animate?.direction === 'left' && 'animate-scaleInLeft',
                isMobile && animate.direction === 'right' && 'animate-scaleInRight')}>
            {/* Header */}
            <div className="flex items-center justify-center md:justify-between pt-6 py-4 md:pt-0 md:py-0">
                <ImageBasic className='w-6 h-6 cursor-pointer' src={IMG.icon.chevronLeftBlack}
                            onClick={handleBack}
                            alt={'chevron left'}/>
                <div className="flex justify-center items-center flex-1 space-x-1">
                    <ImageBasic className='w-5' src={IMG.icon.location} alt='location'/>
                    <div className='font-bold'>{text.changeAddress}</div>
                </div>
            </div>

            {/* Address List */}
            <div ref={addressListRef} className="max-h-[532px] md:max-h-[115px] overflow-y-auto">
                {mergedAddresses?.map((item, index) => {
                    const address = `${item?.address}, ${item?.commune}, ${item?.district}, ${item?.province}`;
                    const isSelected = selectedOption === item?.id;
                    const isLastItem = index === mergedAddresses.length - 1;
                    return (
                        <div
                            key={`${item?.id}_${index}`}
                            ref={isLastItem ? lastItemRef : (isSelected ? selectedItemRef : null)}
                            className={clsx(
                                "flex items-center w-full py-[10px] px-[10px] md:px-0 space-x-2 border-y md:border-none border-gray-200 border-dashed",
                                isSelected && isMobile && 'bg-[#FFFCF3]'
                            )}
                        >
                            <div className="relative flex justify-start items-center md:min-w-[143px]">
                                <input
                                    type="radio"
                                    value={item?.id}
                                    checked={selectedOption === item?.id}
                                    onChange={() => handleOptionChange(item)}
                                    className="w-4 h-4 opacity-0 absolute cursor-pointer"
                                />
                                <div
                                    className={`w-4 h-4 rounded-full cursor-pointer border flex items-center justify-center ${selectedOption === item?.id ? 'border-green-600' : 'border-gray-400'}`}>
                                    {selectedOption === item?.id &&
                                        <div className="w-3 h-3 rounded-full bg-green-600"></div>}
                                </div>
                                <div className="hidden md:block pl-2 font-bold">{item?.name}</div>
                            </div>
                            <div
                                className='md:flex justify-between items-center md:space-x-4 space-y-2 md:space-y-0'>
                                <div className="md:hidden md:font-bold">{item?.name}</div>
                                <div className="text-gray-500 min-w-[85px]">{item?.phoneNumber}</div>
                                <div className="text-gray-500 md:text-black text-sm line-clamp-1">{address}</div>
                                {item?.note && <div
                                    className='md:hidden text-gray-500 text-xs'>{`Ghi chú: ${item?.note}`}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Add New Address Button */}
            <button
                onClick={handleAddAddress}
                className="flex w-full items-center justify-center p-3 border border-gray-300 rounded-[10px] font-medium"
            >
                <span>{text.addAddressPlus}</span>
            </button>
        </div>
    );
}